
import type { LayoutReferState } from "@/types/image";
import { useEventTracking } from "@/hooks/useEventTracking";
import { getClientId } from "@/hooks/useGenerate";
import { getModuleLabelById } from "@xmiles/holopix-helper";
import { getModelEventTrackType, mapModelSource } from '../index';
import {
  FREE_PERSPECTIVE_DEFAULT_CAMERA,
  buildFreePerspectiveGenerateParams,
  resolveFreePerspectivePresetByLegacyMeta,
  resolveFreePerspectiveSizeOption,
} from "@/views/workspace/components/ModulePanel/FreePerspectivePanel";
import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { usePayStore } from "@/store/pay";
import { message, Modal } from "ant-design-vue";
import { filteringInvalidFields } from "@/utils";
import { ModuleId } from "@xmiles/holopix-types";
import { TEAM_QUOTA_EXCEEDED_CODE, TEAM_QUOTA_EXCEEDED_TITLE, TEAM_QUOTA_EXCEEDED_CONTENT, shouldShowTeamQuotaExceededModal } from "@/utils/teamQuotaWarning";
import { MODEL_PERMISSION_DENIED_CODE, MODEL_PERMISSION_DENIED_CONTENT } from "@/utils/modelPermissionDenied";
import { RenderAspectRatios } from "../options";
import { QwenFreePerspectiveService, PerspectiveType } from '@xmiles/holopix-ai';
import { perspectiveTypeOptions } from '@/assets/artwork/options';
import { useGeneratingVersions } from "@/hooks/useGeneratingVersions";
import { initGenerateResult } from "@/hooks/useGenerate";
import { useTaskQueueIntegration } from "@/hooks/usetaskQueue2";
import { $bus } from "@/mitt";
import { generatePerspectivePrompt } from "@/NotRegisterComponents/PerspectiveAdjuster/utils/perspectivePrompt";
import { h } from "vue";
import SvgIcon from "@/components/SvgIcon/index.vue";

export const FREE_PERSPECTIVE_STORE_MODULE_ID = ModuleId.FreePerspective;
export const QWEN_FREE_PERSPECTIVE_MODULE_ID = ModuleId.QwenFreePerspective;
export const module = QWEN_FREE_PERSPECTIVE_MODULE_ID;

// 生图参数
export const freePerspectiveParams = {
  module,
  layoutReferId: null as null | number,
  layoutRefer: null as null | LayoutReferState,
  viewAngleId: PerspectiveType.Front,
  aspectRatios: RenderAspectRatios.Fast,
  cameraParams: { ...FREE_PERSPECTIVE_DEFAULT_CAMERA },
  viewAngleSource: "preset_click",
  batchSize: 1,
}

function getFreePerspectiveGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const {
    layoutRefer,
    viewAngleId,
    aspectRatios,
    cameraParams,
    viewAngleSource,
    legacyPerspectiveMeta,
  } = artworkStore.freePerspective?.params || freePerspectiveParams; // fallback if store not ready

  const sizeOption = resolveFreePerspectiveSizeOption(
    String(aspectRatios === RenderAspectRatios.High ? "1536" : aspectRatios === RenderAspectRatios.Fast ? "1024" : aspectRatios)
  );
  const legacyPreset = resolveFreePerspectivePresetByLegacyMeta(legacyPerspectiveMeta);

  // 基于当前相机参数实时生成 prompt（如 "<sks> front view eye-level shot medium shot"）
  // 查看详情时不会调用此生成函数，编辑复用时相机参数已从历史导入，prompt 会重新计算
  const prompt = generatePerspectivePrompt({
    azimuth: cameraParams?.horizontalAngle ?? FREE_PERSPECTIVE_DEFAULT_CAMERA.horizontalAngle,
    elevation: cameraParams?.verticalAngle ?? FREE_PERSPECTIVE_DEFAULT_CAMERA.verticalAngle,
    distance: cameraParams?.distance ?? FREE_PERSPECTIVE_DEFAULT_CAMERA.distance,
  });

  const qwenParams = buildFreePerspectiveGenerateParams({
    module: QWEN_FREE_PERSPECTIVE_MODULE_ID,
    originImgUrl: layoutRefer?.image || "",
    size: sizeOption.value,
    viewAngleId: viewAngleId ?? legacyPreset.id,
    cameraParams: cameraParams ?? legacyPreset.cameraParams,
    viewAngleSource,
    legacyPerspectiveMeta,
    prompt, // 添加 prompt 参数
  });

  const params = {
    ...qwenParams,
    source: 1,
    userId: userStore.userInfo.id,
    artworkId: artworkStore.artworkState.id,
    uniqueId: userStore.userInfo.uniqueId,
    layoutRefer,
    layoutReferId: layoutRefer?.id || null,
    batchSize: 1
  } as any;

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  return params;
}

// 开启模块生图
export async function startFreePerspectiveGenerate(
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
) {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();
  const payStore = usePayStore();
  const { set, updateResult, del } = useGeneratingVersions();

  const defaultParams = getFreePerspectiveGenerateParam();
  const params = oldParams ? { ...defaultParams, ...oldParams } : defaultParams;
  params.module = QWEN_FREE_PERSPECTIVE_MODULE_ID;

  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  const clientId = params.clientId || params.apiClientId || getClientId();
  params.apiClientId = clientId;

  const startTime = params.startTime ?? Date.now();
  params.startTime = startTime;
  if (!params.ctime) {
    params.ctime = Date.now();
  }

  // Initialize service - 使用 QwenFreePerspectiveService (moduleId 60) 而不是 FreePerspectiveService (moduleId 33)
  const service = new QwenFreePerspectiveService({
    userId: userStore.userInfo.id,
    artworkId: artworkStore.artworkState.id,
    uniqueId: userStore.userInfo.uniqueId
  });

  // Prepare AI params
  const aiParams = {
    clientId,
    layoutRefer: params.layoutRefer || null,
    layoutReferId: params.layoutReferId ?? params.layoutRefer?.id ?? null,
    originImgUrl: params.originImgUrl,
    cameraParams: params.cameraParams,
    aspectRatios: params.aspectRatios,
    qwenFreePerspectiveParams: params.qwenFreePerspectiveParams,
    module: QWEN_FREE_PERSPECTIVE_MODULE_ID,
    sendType: (params as any).sendType,
    prompt: params.prompt || "",
  };

  // Set initial generating state
  if (artworkStore.freePerspective) {
    artworkStore.freePerspective.isGenerate = true;
    artworkStore.freePerspective.result = { ...initGenerateResult, code: 1 };
  }
  
  // Update generating list
  set({
    module: QWEN_FREE_PERSPECTIVE_MODULE_ID,
    clientId,
    params,
    result: { ...initGenerateResult, code: 1 },
    cancel: () => {
        // service.disconnect(); // This would disconnect all tasks, might be too aggressive
    }
  });

  $bus.emit("hasNewVersion");

  userStore.optimisticallyConsumeActivityRight(QWEN_FREE_PERSPECTIVE_MODULE_ID, clientId);

  options?.onClientIdGenerated?.(clientId);
  reportStartEventTracking(clientId);

  const normalizeError = (error: any) => {
    const code = error?.code ?? error?.status ?? error?.data?.code ?? error?.data?.status;
    const msg = error?.msg ?? error?.message ?? error?.data?.msg ?? error?.data?.message;
    return { code, msg };
  };

  const handleGenerateError = (error: any) => {
    const { code, msg } = normalizeError(error);
    let errorMsg = msg || "生图失败，请重试";

    switch (code) {
      case TEAM_QUOTA_EXCEEDED_CODE:
        if (shouldShowTeamQuotaExceededModal()) {
          Modal.warning({
            title: TEAM_QUOTA_EXCEEDED_TITLE,
            icon: h(SvgIcon, { directory: "others", name: "tip-modal-icon", size: 24, class: "anticon" }),
            content: TEAM_QUOTA_EXCEEDED_CONTENT,
            okText: "我知道了",
            okType: "primary",
            wrapClassName: "custom-tip-modal",
            bodyStyle: { padding: "16px" },
            centered: true,
          });
        }
        return "";
      case -1:
        errorMsg = msg || "生图失败，请重试";
        break;
      case -2:
        errorMsg = msg || "生图失败，提示词含有敏感词";
        break;
      case -3:
        errorMsg = msg || "生图失败，登录已失效";
        break;
      case -100:
        errorMsg = msg || "生图失败，您的算力已不足";
        payStore.ballParams.button = "button_creation_img_not_points";
        userStore.usePayModal({ freeToast: false, powerPackage: true });
        break;
      case -200:
        errorMsg = msg || "生图失败，当前已有任务进行中，请稍后提交";
        payStore.ballParams.button = "button_creation_img_concurrent";
        userStore.usePayModal();
        break;
      case -201:
        errorMsg = msg || (userStore.showVip ? "生图失败，生图数量不符合当前套餐权益" : "生图失败");
        userStore.usePayModal();
        break;
      case -202:
        errorMsg = msg || (userStore.showVip ? "生图失败，倍数下载需vip权益" : "生图失败");
        userStore.usePayModal();
        break;
      case -203:
        errorMsg = msg || (userStore.showVip ? "生图失败，您不能使用会员权益" : "生图失败");
        userStore.usePayModal();
        break;
      case MODEL_PERMISSION_DENIED_CODE:
        errorMsg = MODEL_PERMISSION_DENIED_CONTENT;
        break;
      default:
        errorMsg = msg || "生图失败，请重试";
        break;
    }

    message.error(errorMsg);
    return errorMsg;
  };

  try {
    // 新队列集成
    const integration = useTaskQueueIntegration();

    integration.handleOnWebSocketInited(clientId, QWEN_FREE_PERSPECTIVE_MODULE_ID, params);

    const task = service.generate(aiParams);
    
    task.onProgress((result: any) => {
      // 新队列集成 - 处理生成中状态
      integration.handleOnGenerating(clientId);

      const updateData: any = {
        msg: result.msg || '生成中...',
        progress: Math.floor(result.progress || 0),
        code: 2 // Generating
      };
      updateResult(clientId, updateData);
    });

    task.onComplete((result: any) => {
      // 新队列集成 - 处理完成状态
      integration.handleOnSuccess(clientId);

      const images = result.data?.images?.map((img: any) => ({
        id: img.id,
        image: img.image,
        width: img.width || 0,
        height: img.height || 0,
        type: 0,
        collect: 0,
        size: 0,
        param: null,
        artworkId: artworkStore.artworkState.id,
        versionId: 0,
        artworkName: "",
        redrawWeight: null
      })) || [];

      const finalResult = {
        data: {
          status: 200,
          images: images as any,
          param: JSON.stringify(params)
        },
        msg: '生成成功',
        code: 3 as const // Completed
      };

      // Update generating list
      updateResult(clientId, finalResult);

      // Update artwork store result
      if (artworkStore.freePerspective) {
        artworkStore.freePerspective.isGenerate = false;
        artworkStore.freePerspective.result = { ...finalResult } as any;
      }

      // 通知历史记录面板刷新
      $bus.emit("hasNewVersion");

      userStore.finalizeActivityRightDeduction(clientId);
      userStore.queryUserRights();
      userStore.queryActivityRights();
      const imageIds = images.map((img: any) => img.id).join(",");
      reportFinishEventTracking({ status: 200, timeCost: Date.now() - startTime, imageIds });

      options?.onClientIdCleared?.();
    });

    task.onError((err: any) => {
      const errorMsg = handleGenerateError(err);
      
      // 新队列集成 - 处理错误状态
      integration.handleOnError(clientId, errorMsg);

      const errorResult = {
        msg: errorMsg,
        code: -1 as const // Error
      };
      del(clientId);

      if (artworkStore.freePerspective) {
        artworkStore.freePerspective.isGenerate = false;
        artworkStore.freePerspective.result = { ...errorResult } as any;
      }

      userStore.rollbackActivityRightDeduction(clientId);
      reportFinishEventTracking({ status: 500, msg: errorMsg });
      options?.onClientIdCleared?.();
    });

  } catch (error: any) {
      console.error(error);
      const errorMsg = handleGenerateError(error);

      // 新队列集成 - 处理错误状态
      const integration = useTaskQueueIntegration();
      integration.handleOnError(clientId, errorMsg);

      del(clientId);
      if (artworkStore.freePerspective) {
           artworkStore.freePerspective.isGenerate = false;
           artworkStore.freePerspective.result = { msg: errorMsg, code: -1 } as any;
      }
      userStore.rollbackActivityRightDeduction(clientId);
      reportFinishEventTracking({ status: 500, msg: errorMsg });
      options?.onClientIdCleared?.();
  }

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {
    const viewAngleLabel = perspectiveTypeOptions.find(i => i.value === params.qwenFreePerspectiveParams?.viewAngleId)?.label || String(params.qwenFreePerspectiveParams?.viewAngleId);
    const resolutionLabel = `${params.aspectRatios}`;

    const eventValue: any = {
      client_id: clientId,
      source: "直接生成",
      module: getModuleLabelById(QWEN_FREE_PERSPECTIVE_MODULE_ID) as string,
      artwork_id: artworkStore.artworkState.id.toString(),
      batchSize: params.batchSize,
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      model_source: mapModelSource(getModelEventTrackType(artworkStore.artworkState.modelList || [])),
      model_id: String((artworkStore.artworkState.modelList || []).map((item: any) => item.id).join(',')),
      view_angle: viewAngleLabel,
      resolution: resolutionLabel
    };

    useEventTracking({
      event: "creation_event",
      eventValue
    });
  }

  // 生图完成埋点
  function reportFinishEventTracking(data: any) {
    const viewAngleLabel = perspectiveTypeOptions.find(i => i.value === params.qwenFreePerspectiveParams?.viewAngleId)?.label || String(params.qwenFreePerspectiveParams?.viewAngleId);
    const resolutionLabel = `${params.aspectRatios}`;

    const eventValue: any = {
      module: getModuleLabelById(QWEN_FREE_PERSPECTIVE_MODULE_ID) as string,
      client_id: clientId,
      source: "直接生成",
      img_id: data.imageIds || "",
      result: data.status === 200 ? 1 : 0,
      time_cost: data.timeCost,
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      view_angle: viewAngleLabel,
      resolution: resolutionLabel
    };

    if (data.status !== 200) {
      eventValue.fail_reason = data.msg;
    }

    useEventTracking({
      event: "artwork_status_event",
      eventValue
    });
  }
}
